<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useHead } from '@vueuse/head'

import { useClient } from '@base/api/useClient'
import { communityRouteNames } from '@player/community/router/routeNames'
import { useToastsStore } from '@base/store/toastsStore'
import { playerTitle } from '@player/_lib/playerTitle'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'

import { SortOrder, ResourceSortField } from '__generated__/globalTypes'
import {
  listResources,
  listResources_listResources_ListResourcesOutput_resources
} from '__generated__/listResources'
import {
  listResourceTags,
  listResourceTags_listResourceTags_ListResourceTagsOutput_resourceTags
} from '__generated__/listResourceTags'

import { LIST_RESOURCES_QUERY } from '@base/api/queries/listResources'
import { LIST_RESOURCE_TAGS_QUERY } from '@base/api/queries/listResourceTags'

import CardGridResource from '@base/components/CardGridResource/CardGridResource.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import TagPillStacked from '@base/components/TagPillStacked/TagPillStacked.vue'
import USHeroBanner from '@base/components/USHeroBanner/USHeroBanner.vue'
import USPagination from '@base/components/USPagination/USPagination.vue'

import { Card, CardType, CardResourceShare } from '@base/_types/Card'
import { TagPill } from '@base/_types/TagPill'
import { Toast, ToastSemantic } from '@base/_types/Toast'

import { props as propDefinition } from './props'

import heroBannerImage from '@player/_assets/community-cover.jpg'

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const queryKey = 'tags'

useHead({ title: playerTitle('Community') })

const toastsStore = useToastsStore()
const toastsMap: { [key: string]: Toast } = {
  fetchPostsServerError: {
    content: 'Something went wrong fetching the posts',
    semantic: ToastSemantic.Error,
    icon: 'exclamation-mark'
  },
  fetchTagsServerError: {
    content: 'Something went wrong fetching the tags',
    semantic: ToastSemantic.Error,
    icon: 'exclamation-mark'
  }
}

// Inputs
const maxPage = ref<number>(1)
const limit = 12
const currentPage = ref<number>(1)
currentPage.value = props.page || 1

// Outputs
const loading = ref<boolean>(false)
let queryOptions: string[] = []
let selectedOptions = ref<string[]>([])
let availableTags = ref<TagPill[]>([])
const posts = ref<Card[]>([])
let shareUrl = '#'

// Helper Functions
const reset = () => {
  loading.value = false
}

const handlePagination = (page: number) => {
  currentPage.value = page
  router.push({ params: { page: page } })
}

const handleToastSuccess = () => {
  toastsStore.pushToast({
    content: 'The URL has been copied to clipboard!',
    icon: 'check-circle',
    semantic: ToastSemantic.Success
  })
}

const updatePillsFromRoute = () => {
  // Get selected options from route
  queryOptions = route.query[queryKey]
    ? (route.query[queryKey] as string).split(',')
    : []

  // Update pills
  selectedOptions.value = [...queryOptions]
}

const updateRouteFromPills = (selectedOptionsViaPills: string[]) => {
  const newRoute = { ...route }
  newRoute.query = { ...route.query }

  if (currentPage.value > 1) {
    newRoute.params = { page: '1' }
  }

  if (selectedOptionsViaPills.length) {
    newRoute.query[queryKey] = selectedOptionsViaPills.join(',')
  } else {
    delete newRoute.query[queryKey]
  }

  if (!newRoute.path.length) {
    newRoute.path = '/'
  }

  router.replace(newRoute)
}

// Query Functions
const fetchResourceTags = async (): Promise<
  listResourceTags_listResourceTags_ListResourceTagsOutput_resourceTags[] | null
> => {
  try {
    loading.value = true

    const { data } = await client.query<listResourceTags>({
      query: LIST_RESOURCE_TAGS_QUERY,
      fetchPolicy: 'no-cache'
    })

    if (
      data.listResourceTags &&
      data.listResourceTags.__typename === 'ListResourceTagsOutput' &&
      data.listResourceTags.resourceTags
    ) {
      return data.listResourceTags.resourceTags
    }

    toastsStore.pushToast(toastsMap.fetchTagsServerError)
    return null
  } catch (e) {
    console.error(e)
    toastsStore.pushToast(toastsMap.fetchTagsServerError)
    return null
  }
}

const fetchResourcePosts = async (): Promise<
  listResources_listResources_ListResourcesOutput_resources[] | null
> => {
  try {
    const offset = (currentPage.value - 1) * limit
    loading.value = true

    const selectedTagIds: string[] = []
    availableTags.value.forEach((availableTag) => {
      if (selectedOptions.value.includes(availableTag.value)) {
        selectedTagIds.push(availableTag.id)
      }
    })

    const { data } = await client.query<listResources>({
      query: LIST_RESOURCES_QUERY,
      variables: {
        tagIds: selectedTagIds.length ? selectedTagIds : undefined,
        sortOrder: SortOrder.DESC,
        sortField: ResourceSortField.DATE,
        limit: limit,
        offset: offset
      },
      fetchPolicy: 'no-cache'
    })

    if (
      data.listResources &&
      data.listResources.__typename === 'ListResourcesOutput' &&
      data.listResources.resources
    ) {
      maxPage.value = Math.ceil(data.listResources.count / limit)
      return data.listResources.resources
    }

    toastsStore.pushToast(toastsMap.fetchPostsServerError)
    return null
  } catch (e) {
    console.error(e)
    toastsStore.pushToast(toastsMap.fetchPostsServerError)
    return null
  }
}

const fetchData = async () => {
  reset()

  // Prepare Inputs + any Formatting

  // Validate Inputs (optional, mainly for validating form inputs)

  // Fetch Data
  const resourceTags = await fetchResourceTags()
  if (resourceTags) {
    availableTags.value = resourceTags.map((resourceTag) => {
      const newTag: TagPill = {
        id: resourceTag.id,
        label: resourceTag.label,
        name: resourceTag.id,
        value: resourceTag.label.replace(/ /g, '-') // user friendly label
      }

      return newTag
    })
  }

  const resourcePosts = await fetchResourcePosts()
  if (resourcePosts) {
    posts.value = resourcePosts.map((resourcePost) => {
      const tags = resourcePost.tags.map((tag) => tag.label)

      const share: CardResourceShare = {
        url: shareUrl
      }

      if (resourcePost.video) {
        return {
          type: CardType.CardResourceVideo,
          data: {
            tags: tags,
            date: new Date(resourcePost.date),
            heading: resourcePost.body,
            video: {
              src: ugcImageSrc(resourcePost.video)[0]
            },
            share: share,
            showMenu: false
          }
        }
      } else if (
        resourcePost.galleryPaths &&
        resourcePost.galleryPaths.length
      ) {
        return {
          type: CardType.CardResourceGallery,
          data: {
            tags: tags,
            date: new Date(resourcePost.date),
            heading: resourcePost.body,
            images: resourcePost.galleryPaths?.map((galleryPath) => {
              return { src: ugcImageSrc(galleryPath)[0] }
            }),
            share: share,
            showMenu: false
          }
        }
      } else {
        return {
          type: CardType.CardResourceText,
          data: {
            tags: tags,
            date: new Date(resourcePost.date),
            text: resourcePost.body,
            share: share,
            showMenu: false
          }
        }
      }
    })
  }

  loading.value = false
}
onBeforeRouteUpdate(async () => {
  await fetchData()
  loading.value = true
  loading.value = false
})

watch(props, async () => {
  await fetchData()
})

//
watch(
  () => selectedOptions.value,
  async (currentValue) => {
    updateRouteFromPills(currentValue)
    await fetchData()
  }
)

// Fetch data when the view is first rendered
updatePillsFromRoute()
await fetchData()

onMounted(() => {
  const host = window.location.host
  const route = router.resolve({
    name: communityRouteNames.CommunityBoard
  }).href

  for (let post of posts.value) {
    post.data.share.url = `${host}${route}`
  }
})
</script>

<template>
  <div class="community-board">
    <USHeroBanner class="community-board__hero-banner">
      <ImageWrapper
        :src="heroBannerImage"
        object-fit="cover"
        object-position="50% 40%"
        height="100%"
      />

      <div class="community-board__hero-banner-content">
        <div class="grid__row">
          <div class="grid__column grid__column--12">
            <div class="font-color--neutral-a5 text-align--center">
              <p class="display--d2--semibold margin-bottom--8">
                See what's happening around Alberta
              </p>
              <p class="display--d1">Community</p>
            </div>
          </div>
        </div>
      </div>
    </USHeroBanner>

    <div class="grid__row community-board__row-containing-tags">
      <div
        class="grid__column grid__column--12 grid__column--breakpoint--9--10 grid__column--breakpoint--9--offset--1"
      >
        <div class="community-board__tags-wrapper">
          <TagPillStacked v-model="selectedOptions" :tags="availableTags" />
        </div>
      </div>
    </div>

    <CardGridResource :cards="posts" @shareSuccuss="handleToastSuccess" />
    <div>
      <USPagination
        v-if="maxPage > 1"
        :min="1"
        :max="maxPage"
        :current="currentPage"
        class="community-board__pagination"
        @page-goto="handlePagination"
        @page-next="handlePagination"
        @page-previous="handlePagination"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

$search-bar-offset-sm: 30px;
$search-bar-offset-lg: 50px;

.community-board {
  &__hero-banner {
    margin-top: calc(-1 * var(--spacing--5));
  }

  &__hero-banner-content {
    position: absolute;
    z-index: 2;
    width: 100%;
    top: 50%;
    transform: translateY(-100%);
  }

  &__tags-wrapper {
    background: var(--color--neutral-a5);
    border-radius: 20px;
    box-shadow: var(--box-shadow--b1);
    padding: var(--spacing--6);

    @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
      border-radius: 30px;
    }
  }

  &__row-containing-tags {
    margin-bottom: var(--spacing--5);
    margin-top: #{$search-bar-offset-sm * -1};

    @include breakpoint.min-width(breakpoint.$breakpoint--9) {
      margin-top: #{$search-bar-offset-lg * -1};
    }
  }

  &__pagination {
    margin-top: calc(var(--spacing--5) - (var(--grid--gutter-width) / 2));
  }
}
</style>
